import React from 'react';
import { withRouter, Switch, Redirect, Route } from 'react-router-dom';
import exact from 'prop-types-exact';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, lifecycle } from 'recompose';
import queryString from 'query-string';
import { routerProps } from '../propTypes';

import DefaultLayout from '../layouts/DefaultLayout';

import Header from './Header/index';
import LoginPage from './Login/page';
import TimesheetNavigatorPage from '../features/timesheetNavigator/page';

import TimesheetSubmissionProcessor from '../layouts/TimesheetSubmissionProcessor/index';
import UserListPage from '../features/admin/userList/page';
import EquipmentListPage from '../features/admin/equipmentList/page';
import LeaseListPage from '../features/admin/leaseList/page';
import OperatorListPage from '../features/admin/operatorList/page';
import WellListPage from '../features/admin/wellList/page';
import WeeklyHoursPage from './WeeklyHoursReport/page';
import WeeklyStatusPage from '../features/timesheetCollectionStatus/page';
import TimesheetApprovalsContainerEnhanced from '../features/timesheetApprovals/containers/TimesheetApprovalsContainerEnhanced';
import TicketCollectionStatusPage from '../features/ticketNumberStatus/page';
import TicketTable from '../features/ticketTable/page';
import TicketInvoicingPage from '../features/ticketInvoicing/page';
import TicketApprovalsPage from '../features/ticketApprovals/page';
import PayrollProcessorPage from '../features/payrollProcessor/page';
import TimesheetItemViewer from '../features/timesheetViewer/page';
import JobViewerPage from '../features/ticketViewer/page';
import PassResetRequest from '../features/passwordReset/components/PasswordResetRequest/PasswordResetRequest';
import PassReset from '../features/passwordReset/components/PasswordReset/PasswordReset';
import BonusesPage from '../features/bonuses/page';
import BonusSummaryPage from '../features/bonusSummary/page';
import BonusApprovalsPage from '../features/bonusApprovals/page';
import MissingTicketNumbersPage from '../features/missingTicketNumbers/page';
import PerDiemPage from '../features/perDiems/page';
import PerDiemPaidNotWorkedPage from '../features/perDiems/containers/PerDiemsPaidNotWorkedPage';
import PerDiemApprovalPage from '../features/perDiems/containers/PerDiemApprovalPage';
import RedirectLandingPage from '../features/authentication/containers/RedirectLandingPage/index';
import AllPerDiemsPage from '../features/perDiems/containers/AllPerDiemsPage';
import TrirReportPage from '../features/trirReport/page';
import UnaccountedHoursTicketsPage from '../features/unaccountedHoursTicketsReport/page';
import ExportDeferredJobsToolPage from '../features/ticketInvoicing/ExportDeferredJobsTool/page';
import PerDiemReportPage from '../features/perDiemReport/page';
import FieldAdminStagingPage from '../features/fieldAdminReview/page';
import GuaranteedHoursPage from '../features/guaranteedHoursReport/page';
import AccrualPage from '../features/accrualReport/page';
import EquipmentUsagePage from '../features/equipmentUsageReport/page';
import OnetimePasswordPage from '../features/admin/requestMobilePassword/page';

// eslint-disable-next-line import/first
import './App.css';

import PrivateRoute from '../features/authentication/containers/PrivateRoute';
import { getAuthenticated } from '../features/authentication/selectors';
import { setShowHeader } from '../app/actions';

const propTypes = exact({
  setShowHeaderAction: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  ...routerProps,
});

export const App = (props) => {
  const { isAuthenticated } = props;

  return (
    <React.Fragment>
      {/* FIXME: Header needs to be relocated outside of the main router switch */}
      {/* For now, I am leaving it to focus on larger issues. */}
      {isAuthenticated && <Header />}
      <Switch>
        <Route exact path="/login" component={LoginPage} />
        <Route exact path="/password-reset/request" component={PassResetRequest} />
        <Route exact path="/password-reset" component={PassReset} />
        <PrivateRoute
          exact
          path="/submissions/processor"
          component={TimesheetSubmissionProcessor}
        />
        <PrivateRoute
          exact
          path="/submissions/timesheets"
          component={TimesheetNavigatorPage}
        />
        <PrivateRoute
          exact
          path="/submissions/timesheets/details/:id"
          component={TimesheetItemViewer}
        />
        <PrivateRoute exact path="/submissions/tickets" component={TicketTable} />
        <PrivateRoute
          exact
          path="/browse/jobs/:jobId"
          component={JobViewerPage}
        />
        <PrivateRoute
          exact
          path="/submissions/status/timesheets"
          component={WeeklyStatusPage}
        />
        <PrivateRoute
          exact
          path="/submissions/status/tickets"
          component={TicketCollectionStatusPage}
        />
        <PrivateRoute
          exact
          path="/submissions/status/gap_detector"
          component={() => {
            return <DefaultLayout render={() => { return <MissingTicketNumbersPage />; }} />;
          }}
        />
        <PrivateRoute
          exact
          path="/review/tickets"
          component={FieldAdminStagingPage}
        />
        <PrivateRoute
          exact
          path="/approvals/timesheets"
          component={TimesheetApprovalsContainerEnhanced}
        />
        <PrivateRoute
          exact
          path="/approvals/tickets"
          component={() => {
            return <DefaultLayout render={() => { return <TicketApprovalsPage />; }} />;
          }}
        />
        <PrivateRoute exact path="/reports/weekly-hours" component={WeeklyHoursPage} />
        <PrivateRoute exact path="/reports/trir" component={TrirReportPage} />
        <PrivateRoute exact path="/reports/unaccounted-hours-tickets" component={UnaccountedHoursTicketsPage} />
        <PrivateRoute exact path="/reports/guaranteed-hours" component={GuaranteedHoursPage} />
        <PrivateRoute exact path="/reports/equipment-usage" component={EquipmentUsagePage} />

        <PrivateRoute
          exact
          path="/accounting/payroll"
          component={() => { return <DefaultLayout render={PayrollProcessorPage} />; }}
        />
        <PrivateRoute
          exact
          path="/accounting/invoicing"
          component={() => {
            return <DefaultLayout render={() => { return <TicketInvoicingPage />; }} />;
          }}
        />
        <PrivateRoute exact path="/accounting/accrual-report" component={AccrualPage} />

        <PrivateRoute exact path="/admin/users" component={UserListPage} />
        <PrivateRoute exact path="/admin/equipment" component={EquipmentListPage} />
        <PrivateRoute exact path="/admin/leases" component={LeaseListPage} />
        <PrivateRoute exact path="/admin/operators" component={OperatorListPage} />
        <PrivateRoute exact path="/admin/wells" component={WellListPage} />
        <PrivateRoute exact path="/admin/mobile-password" component={OnetimePasswordPage} />

        <PrivateRoute exact path="/bonuses" component={BonusesPage} />
        <PrivateRoute exact path="/accounting/bonuses" component={BonusSummaryPage} />
        <PrivateRoute exact path="/approvals/bonuses" component={BonusApprovalsPage} />

        {/* NOTE: Request routes can't be exact or they won't route to confirm/results pages */}
        <PrivateRoute path="/per-diem/requests" component={PerDiemPage} />
        <PrivateRoute path="/per-diem/requests/admin" component={PerDiemPage} />

        <PrivateRoute exact path="/per-diem/paid-not-worked" component={PerDiemPaidNotWorkedPage} />
        <PrivateRoute exact path="/per-diem/approvals" component={PerDiemApprovalPage} />
        <PrivateRoute exact path="/per-diem/history" component={AllPerDiemsPage} />
        <PrivateRoute exact path="/per-diem/summary" component={PerDiemReportPage} />

        <PrivateRoute exact path="/export-deferred-jobs" component={ExportDeferredJobsToolPage} />

        <PrivateRoute exact path="/" component={RedirectLandingPage} />
        <Redirect to="/" />
      </Switch>
    </React.Fragment>
  );
};

App.propTypes = propTypes;

const withInitPage = lifecycle({
  componentWillMount() {
    const { setShowHeaderAction, location } = this.props;

    const querySringObject = queryString.parse(location.search);

    const { showHeader } = querySringObject;
    if (showHeader) {
      setShowHeaderAction(showHeader);
    }
  },
});

const mapStateToProps = (state) => {
  return {
    isAuthenticated: getAuthenticated(state),
  };
};

const mapDispatchToProps = {
  setShowHeaderAction: setShowHeader,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

const enhance = compose(
  withRouter,
  withRedux,
  withInitPage,
);

export default enhance(App);
